/**
 * God's Vision Map — scoring engine (frontend mirror).
 *
 * This is a deliberate, intentional duplicate of api/scoring.py's formula so
 * the PWA can compute a record's overall score instantly, fully offline,
 * without a round trip to the backend. If you change the formula here,
 * change it there too.
 *
 * Formula: overall_score = sum(score[dimension] * weight[dimension])
 * Each dimension score is an integer 1-10. Weights sum to 1.0 (100%).
 */
import { DIMENSIONS, DEFAULT_WEIGHTS, type Dimension, type Weights } from './types';

const WEIGHT_TOLERANCE = 0.001;

export function validateWeights(weights: Weights): void {
  const missing = DIMENSIONS.filter((d) => !(d in weights));
  if (missing.length) {
    throw new Error(`Missing weights for: ${missing.join(', ')}`);
  }
  const total = DIMENSIONS.reduce((sum, d) => sum + Number(weights[d]), 0);
  if (Math.abs(total - 1.0) > WEIGHT_TOLERANCE) {
    throw new Error(`Weights must sum to 1.0 (got ${total.toFixed(4)})`);
  }
}

export function calculateOverallScore(
  scores: Partial<Record<Dimension, number | null | undefined>>,
  weights: Weights = DEFAULT_WEIGHTS,
): number {
  let total = 0;
  for (const dim of DIMENSIONS) {
    const raw = scores[dim];
    const value = raw === null || raw === undefined ? 0 : Number(raw);
    const w = weights[dim] ?? DEFAULT_WEIGHTS[dim];
    total += value * w;
  }
  return Math.round(total * 100) / 100;
}
